import type { PsHttpSSE } from '@patsnap-utils/sse';
import type { NoveltySearchApiEnvelope } from './novelty-search-api-response';

export const NoveltySearchApi = Symbol('NoveltySearchApi');

/** 与 telecom task/init 一致；task_id 生成方式同 EssentialityClaim 等（crypto.randomUUID） */
export interface CheckTelecomDomainRequest {
    task_id: string;
    text: string;
    start_date?: string;
    end_date?: string;
    model?: '' | 'deepseek' | 'qwen';
}

/** data：是否为电信领域技术方案 */
export interface CheckTelecomDomainResponse
    extends NoveltySearchApiEnvelope<
        | boolean
        | {
            input_type?: 'pn' | 'text';
            inputType?: 'pn' | 'text';
        }
    > {}

export interface ExtractInventionsRequest {
    task_id: string;
}

export interface AddInventionRequest {
    task_id: string;
    title: string;
    description: string;
}

export interface AddInventionResponse extends NoveltySearchApiEnvelope<unknown> {
    success?: boolean;
    message?: string;
}

export interface ExtractInventionsResponse
    extends NoveltySearchApiEnvelope<{
        /** 「AI 分析完成」折叠区正文 */
        analysis?: string;
        inventions?: Array<{
            /** 卡片标题行 */
            title?: string;
            description: string;
            sources?: Array<{
                text?: string;
                segment_id?: number;
                char_start?: number;
                char_end?: number;
            }>;
            /** 默认选中的推荐发明点 */
            is_primary?: boolean;
            reasoning?: string;
            type?: string;
            benefit?: string;
        }>;
        original_text?: string;
    }> {}

/** 通用 SSE 单条事件 data：status=FINISH 时 content 为最终结果 */
export interface NoveltySearchSseEventPayload<T = unknown> {
    status: string;
    content?: T;
}

/** 通用 SSE 单条事件 envelope */
export interface NoveltySearchSseEvent<T = unknown>
    extends NoveltySearchApiEnvelope<NoveltySearchSseEventPayload<T>> {}

/** extract_inventions_sse 单条事件 */
export type ExtractInventionsSseEvent = NoveltySearchSseEvent<ExtractInventionsResponse['data']>;
export type ExtractInventionsSseEventPayload = NoveltySearchSseEventPayload<ExtractInventionsResponse['data']>;

export interface ExtractInventionsSseThinking {
    type: 'thinking';
    content: string;
}

export interface ExtractInventionsSseResult {
    type: 'result';
    content: {
        success?: boolean;
        data?: ExtractInventionsResponse['data'];
        error_code?: number;
        error_msg?: string;
        message?: string;
    };
}

export interface ExtractInventionsSseDone {
    type: 'done';
}

export type ExtractInventionsSseMessage =
    | ExtractInventionsSseThinking
    | ExtractInventionsSseResult
    | ExtractInventionsSseDone;

export interface ExtractClaimRequest {
    task_id: string;
}

export type ExtractClaimData = {
    analysis?: string;
    patent_number?: string;
    patentNumber?: string;
    pn?: string;
    title?: string;
    patent_title?: string;
    claims?: unknown[];
    claim_options?: unknown[];
    independent_claims?: unknown[];
    inventions?: Array<{
        title?: string;
        description?: string;
        sources?: unknown[];
        is_primary?: boolean;
        source_segments?: unknown[];
    }>;
    original_text?: string;
    claim_text?: string;
};

export interface ExtractClaimResponse extends NoveltySearchApiEnvelope<ExtractClaimData> {}

export interface ExtractClaimSseThinking {
    type: 'thinking';
    content: string;
}

export interface ExtractClaimSseResult {
    type: 'result';
    content: {
        success?: boolean;
        data?: ExtractClaimData;
        error_code?: number;
        error_msg?: string;
        message?: string;
    };
}

export interface ExtractClaimSseDone {
    type: 'done';
}

export type ExtractClaimSseMessage =
    | ExtractClaimSseThinking
    | ExtractClaimSseResult
    | ExtractClaimSseDone;

export interface PredictWorkingGroupRequest {
    text: string;
}

export interface PredictWorkingGroupResponse
    extends NoveltySearchApiEnvelope<{
        working_groups?: Array<{ name: string }>;
    }> {}

export interface ExtractSearchElementsRequest {
    inventions: Array<{ type: string; description: string }>;
}

export interface ExtractSearchElementsResponse
    extends NoveltySearchApiEnvelope<{
        search_keywords?: string[];
    }> {}

export interface ExtractFeaturesRequest {
    task_id: string;
    title: string;
    description: string;
}

export type ExtractFeaturesData = {
    analysis?: string;
    features?: unknown[];
    feature_decomposition?: unknown[];
    feature_items?: unknown[];
    extracted_features?: unknown[];
    original_text?: string;
    technical_solution?: string;
};

export interface ExtractFeaturesResponse extends NoveltySearchApiEnvelope<ExtractFeaturesData> {}

export interface ExtractFeaturesSseThinking {
    type: 'thinking';
    content: string;
}

export interface ExtractFeaturesSseResult {
    type: 'result';
    content: {
        success?: boolean;
        data?: ExtractFeaturesData;
        error_code?: number;
        error_msg?: string;
        message?: string;
    };
}

export interface ExtractFeaturesSseDone {
    type: 'done';
}

export type ExtractFeaturesSseMessage =
    | ExtractFeaturesSseThinking
    | ExtractFeaturesSseResult
    | ExtractFeaturesSseDone;

/** 检索策略页：合并预测 WG + 检索关键词 */
export interface PredictWgAndKeywordsRequest {
    task_id: string;
    features?: Array<{
        feature_id: string;
        feature_text_original: string;
        selected: true;
    }>;
}

export interface PredictWgAndKeywordsResponse
    extends NoveltySearchApiEnvelope<{
        /** 系统建议的 WG 列表（策略页可改为用户多选子集） */
        working_group?: string[];
        /** 可选范围（展示全部） */
        working_group_candidates?: Array<string | {
            key?: string;
            category?: string;
            groups?: string[];
        }>;
        keywords?: Array<string | {
            keyword?: string;
            expansions?: string[];
        }>;
        description?: string;
    }> {}

export interface SearchTdocsRequest {
    task_id: string;
    keywords: string[];
    /** 工作组全名列表，如 ["3GPP RAN1","3GPP RAN2"] */
    working_group: string[];
}

export interface SearchTdocsResponse
    extends NoveltySearchApiEnvelope<{
        solr_num_found?: number;
        emb_num_found?: number;
        results?: unknown[];
    }> {}
export type SearchTdocsSseEvent = NoveltySearchSseEvent<SearchTdocsResponse['data']>;
export type SearchTdocsSseEventPayload = NoveltySearchSseEventPayload<SearchTdocsResponse['data']>;

/** POST …/compare：生成对比表 */
export interface NoveltySearchCompareRequest {
    task_id: string;
    tdoc_numbers: string[];
}

export interface NoveltySearchCompareResponse extends NoveltySearchApiEnvelope<unknown> {}
export type NoveltySearchCompareSseEvent = NoveltySearchSseEvent<NoveltySearchCompareResponse['data']>;
export type NoveltySearchCompareSseEventPayload = NoveltySearchSseEventPayload<NoveltySearchCompareResponse['data']>;

/** POST …/report：检索报告页拉取报告正文等 */
export interface NoveltySearchReportRequest {
    task_id: string;
    /** 特征对比页编辑后的 comparisons（与 compare 返回结构一致） */
    comparisons?: unknown;
}

export interface NoveltySearchReportResponse extends NoveltySearchApiEnvelope<unknown> {}
export type NoveltySearchReportSseEvent = NoveltySearchSseEvent<NoveltySearchReportResponse['data']>;
export type NoveltySearchReportSseEventPayload = NoveltySearchSseEventPayload<NoveltySearchReportResponse['data']>;

/** POST …/export：导出报告文件 */
export interface NoveltySearchExportRequest {
    task_id: string;
}

export interface NoveltySearchExportTelecomRequest {
    task_id: string;
}

/** GET …/task/history?task_id= */
export interface NoveltySearchTaskHistoryParams {
    task_id: string;
}

export type NoveltySearchTaskHistoryResponse = NoveltySearchApiEnvelope<unknown>;

export interface NoveltySearchApi {
    checkTelecomDomain(body: CheckTelecomDomainRequest): Promise<CheckTelecomDomainResponse>;

    extractInventions(body: ExtractInventionsRequest): Promise<ExtractInventionsResponse>;

    /** SSE 接口，内部聚合 FINISH 事件后返回与旧接口一致的数据结构 */
    extractInventionsSse(body: ExtractInventionsRequest): Promise<ExtractInventionsResponse>;

    extractInventionsStream(body: ExtractInventionsRequest): Promise<PsHttpSSE<ExtractInventionsSseMessage>>;

    addInvention(body: AddInventionRequest): Promise<AddInventionResponse>;

    extractClaimSse(body: ExtractClaimRequest): Promise<ExtractClaimResponse>;

    extractClaimStream(body: ExtractClaimRequest): Promise<PsHttpSSE<ExtractClaimSseMessage>>;

    predictWorkingGroup(body: PredictWorkingGroupRequest): Promise<PredictWorkingGroupResponse>;

    extractSearchElements(body: ExtractSearchElementsRequest): Promise<ExtractSearchElementsResponse>;

    extractFeaturesSse(body: ExtractFeaturesRequest): Promise<ExtractFeaturesResponse>;

    extractFeaturesStream(body: ExtractFeaturesRequest): Promise<PsHttpSSE<ExtractFeaturesSseMessage>>;

    predictWgAndKeywords(body: PredictWgAndKeywordsRequest): Promise<PredictWgAndKeywordsResponse>;

    searchTdocs(body: SearchTdocsRequest): Promise<SearchTdocsResponse>;
    searchTdocsSse(body: SearchTdocsRequest): Promise<SearchTdocsResponse>;

    compare(body: NoveltySearchCompareRequest): Promise<NoveltySearchCompareResponse>;
    compareSse(body: NoveltySearchCompareRequest): Promise<NoveltySearchCompareResponse>;

    report(body: NoveltySearchReportRequest): Promise<NoveltySearchReportResponse>;
    reportSse(body: NoveltySearchReportRequest): Promise<NoveltySearchReportResponse>;

    exportReport(body: NoveltySearchExportRequest): Promise<Blob>;

    exportTelecomReport(body: NoveltySearchExportTelecomRequest): Promise<Blob>;

    getTaskHistory(params: NoveltySearchTaskHistoryParams): Promise<NoveltySearchTaskHistoryResponse>;
}
